// Shared open/close lifecycle for the CRM's floating panels (dropdowns, date
// picker). The panel is portalled to <body> with fixed positioning so a
// scrolling or overflow-hidden modal never clips it, and it stays mounted for
// the exit animation before unmounting.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

export type PopoverPhase = 'closed' | 'open' | 'closing'

const EXIT_MS = 120
const GAP = 4
const EDGE = 8

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function usePopover<T extends HTMLElement>({ minWidth = 0 }: { minWidth?: number } = {}) {
  const [phase, setPhase] = useState<PopoverPhase>('closed')
  const [style, setStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<T>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef<PopoverPhase>('closed')
  const timerRef = useRef<number | null>(null)
  phaseRef.current = phase

  const open = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    setPhase('open')
  }, [])

  const close = useCallback((restoreFocus = true) => {
    if (phaseRef.current !== 'open') return
    setPhase('closing')
    timerRef.current = window.setTimeout(() => setPhase('closed'), prefersReducedMotion() ? 0 : EXIT_MS)
    if (restoreFocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
  }, [])

  const place = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom - EDGE
    const above = rect.top - EDGE
    const flip = below < 220 && above > below
    const width = Math.min(Math.max(rect.width, minWidth), window.innerWidth - EDGE * 2)
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE))

    setStyle(
      flip
        ? { left, width, bottom: window.innerHeight - rect.top + GAP, maxHeight: above - GAP }
        : { left, width, top: rect.bottom + GAP, maxHeight: below - GAP },
    )
  }, [minWidth])

  useLayoutEffect(() => {
    if (phase !== 'open') return
    place()
    window.addEventListener('resize', place)
    // Capture catches the modal body scrolling, not just the window.
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [phase, place])

  useEffect(() => {
    if (phase !== 'open') return
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      close(false)
    }
    document.addEventListener('pointerdown', closeOnOutside)
    return () => document.removeEventListener('pointerdown', closeOnOutside)
  }, [phase, close])

  return { phase, isOpen: phase === 'open', open, close, triggerRef, panelRef, style }
}
